import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Sparkles,
  Download,
  MessageSquare,
  Loader2,
  ChevronDown,
  Send,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Save
} from 'lucide-react';
import Sidebar from '../components/Sidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper for generating UUIDs in non-secure contexts
function generateUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export default function EditorPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [samples, setSamples] = useState([]);

  // Playground state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [temperature, setTemperature] = useState([0.7]);

  const sampleCount = samples.length;
  const canExport = sampleCount >= 10;
  const canSimulate = sampleCount >= 10;

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}`);
      const data = response.data;
      setProject(data);
      setName(data.name);
      setSystemPrompt(data.system_prompt || '');
      setSamples(data.samples || []);
    } catch (error) {
      console.error('Failed to fetch project:', error);
      toast.error(language === 'zh' ? '加载项目失败' : 'Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const saveProject = useCallback(async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/projects/${projectId}`, {
        name,
        system_prompt: systemPrompt,
        samples: samples
      });
      toast.success(language === 'zh' ? '保存成功' : 'Saved successfully');
    } catch (error) {
      toast.error(language === 'zh' ? '保存失败' : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [projectId, name, systemPrompt, samples, language]);

  const addSample = () => {
    setSamples([...samples, {
      id: generateUUID(),
      user_content: '',
      assistant_content: ''
    }]);
  };

  const updateSample = (index, field, value) => {
    const newSamples = [...samples];
    newSamples[index] = { ...newSamples[index], [field]: value };
    setSamples(newSamples);
  };

  const deleteSample = (index) => {
    setSamples(samples.filter((_, i) => i !== index));
  };

  const generateSamples = async (count) => {
    if (!systemPrompt.trim()) {
      toast.error(language === 'zh' ? '请先设置系统提示词' : 'Please set system prompt first');
      return;
    }


    setGenerating(true);
    // Auto-save first to ensure backend has latest system prompt
    try {
      await saveProject();
    } catch (err) {
      console.warn('Auto-save failed before generation', err);
      // Continue anyway, but backend might use old prompt
    }

    try {
      const response = await axios.post(`${API_URL}/ai/fill`, {
        project_id: projectId,
        count: count
      });

      const newSamples = response.data.map(s => ({
        id: generateUUID(),
        user_content: s.user_content,
        assistant_content: s.assistant_content
      }));

      setSamples([...samples, ...newSamples]);
      toast.success(language === 'zh' ? `成功生成 ${newSamples.length} 条样本` : `Generated ${newSamples.length} samples`);
    } catch (error) {
      const message = error.response?.data?.detail || (language === 'zh' ? '生成失败' : 'Generation failed');
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const exportJsonl = async () => {
    if (!canExport) {
      toast.error(t('editor.minWarning'));
      return;
    }

    // Save first
    await saveProject();

    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}/export`);
      const { filename, content } = response.data;

      // Create and download file
      const blob = new Blob([content], { type: 'application/jsonl' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(language === 'zh' ? '导出成功' : 'Export successful');
    } catch (error) {
      const message = error.response?.data?.detail || (language === 'zh' ? '导出失败' : 'Export failed');
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || !canSimulate) return;

    // Save samples first
    await saveProject();

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await axios.post(`${API_URL}/ai/playground`, {
        project_id: projectId,
        user_message: userMessage,
        temperature: temperature[0]
      });

      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      const message = error.response?.data?.detail || (language === 'zh' ? '模拟失败' : 'Simulation failed');
      toast.error(message);
      setChatMessages(prev => prev.slice(0, -1)); // Remove user message on error
    } finally {
      setChatLoading(false);
    }
  };

  const getStatusInfo = () => {
    if (sampleCount < 10) {
      return {
        icon: AlertCircle,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        message: t('editor.minWarning')
      };
    }
    if (sampleCount < 20) {
      return {
        icon: AlertTriangle,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        message: t('editor.recommendMore')
      };
    }
    return {
      icon: CheckCircle,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      message: t('editor.goodCount')
    };
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <main className="content-container flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </main>
      </div>
    );
  }

  return (
    <div className="main-layout" data-testid="editor-page">
      <Sidebar />

      <main className="content-container">
        {/* Header */}
        <div className="glass-header sticky top-0 z-10 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="text-xl font-semibold border-0 bg-transparent p-0 h-auto focus:ring-0"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  data-testid="project-name-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Sample count badge */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${statusInfo.bgColor} ${statusInfo.borderColor} border`}>
                <StatusIcon className={`w-4 h-4 ${statusInfo.color}`} />
                <span className={`text-sm font-medium ${statusInfo.color}`}>
                  {t('editor.sampleCount')}: {sampleCount}
                </span>
              </div>

              <Button
                variant="outline"
                onClick={saveProject}
                disabled={saving}
                data-testid="save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {t('common.save')}
              </Button>

              <Button
                variant="outline"
                onClick={exportJsonl}
                disabled={!canExport || exporting}
                className={!canExport ? 'opacity-50 cursor-not-allowed' : ''}
                data-testid="export-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {t('editor.export')}
              </Button>
            </div>
          </div>

          {/* Status message */}
          {sampleCount < 10 && (
            <div className="mt-3 flex items-center gap-2 text-red-600 text-sm" data-testid="warning-message">
              <AlertCircle className="w-4 h-4" />
              {statusInfo.message}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="editor-layout">
          {/* Left Panel - Data Entry */}
          <div className="editor-left">
            {/* System Prompt */}
            <div className="mb-6">
              <Label className="text-base font-semibold mb-2 block" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {t('editor.systemPrompt')}
              </Label>
              <Textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder={t('editor.systemPromptPlaceholder')}
                className="min-h-[100px] input-focus-ring"
                data-testid="system-prompt-input"
              />
            </div>

            {/* Samples */}
            <div className="flex items-center justify-between mb-4">
              <Label className="text-base font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {t('editor.samples')} ({sampleCount})
              </Label>
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      className="btn-ai-magic"
                      disabled={generating}
                      data-testid="ai-fill-btn"
                    >
                      {generating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('editor.generating')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          {t('editor.aiFill')}
                          <ChevronDown className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => generateSamples(10)} data-testid="generate-10-btn">
                      {t('editor.generate10')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => generateSamples(20)} data-testid="generate-20-btn">
                      {t('editor.generate20')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button variant="outline" onClick={addSample} data-testid="add-sample-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('editor.addSample')}
                </Button>
              </div>
            </div>

            <ScrollArea className="h-[calc(100vh-320px)]">
              <div className="space-y-3 pr-4">
                {samples.map((sample, index) => (
                  <div key={sample.id} className="sample-row" data-testid={`sample-row-${index}`}>
                    <div className="flex items-start gap-4">
                      <div className="flex-1 space-y-3">
                        <div>
                          <Label className="text-xs text-slate-500 mb-1 block">
                            {t('editor.userInput')}
                          </Label>
                          <Textarea
                            value={sample.user_content}
                            onChange={(e) => updateSample(index, 'user_content', e.target.value)}
                            placeholder={language === 'zh' ? '用户问题...' : 'User question...'}
                            className="min-h-[60px] text-sm input-focus-ring"
                            data-testid={`sample-${index}-user-input`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500 mb-1 block">
                            {t('editor.assistantResponse')}
                          </Label>
                          <Textarea
                            value={sample.assistant_content}
                            onChange={(e) => updateSample(index, 'assistant_content', e.target.value)}
                            placeholder={language === 'zh' ? '助手回复...' : 'Assistant response...'}
                            className="min-h-[60px] text-sm input-focus-ring"
                            data-testid={`sample-${index}-assistant-input`}
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => deleteSample(index)}
                        className="p-2 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded transition-colors"
                        data-testid={`sample-${index}-delete-btn`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {samples.length === 0 && (
                  <div className="text-center py-12 text-slate-400">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{language === 'zh' ? '暂无样本，点击添加或使用 AI 填充' : 'No samples yet. Add manually or use AI Auto-Fill'}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Right Panel - Playground */}
          <div className="editor-right">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {t('playground.title')}
              </h3>
              <p className="text-sm text-slate-500">{t('playground.subtitle')}</p>
            </div>

            {/* Temperature slider */}
            <div className="mb-4 p-3 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">{t('playground.temperature')}</Label>
                <span className="text-sm font-mono text-slate-600">{temperature[0].toFixed(1)}</span>
              </div>
              <Slider
                value={temperature}
                onValueChange={setTemperature}
                min={0}
                max={1}
                step={0.1}
                className="w-full"
                data-testid="temperature-slider"
              />
            </div>

            {/* Chat container */}
            <div className="chat-container flex-1">
              {!canSimulate ? (
                <div className="flex-1 flex items-center justify-center p-6">
                  <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">{t('playground.noSamples')}</p>
                  </div>
                </div>
              ) : (
                <>
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {chatMessages.map((msg, index) => (
                        <div
                          key={index}
                          className={msg.role === 'user' ? 'message-user' : 'message-assistant'}
                        >
                          <div className={`message-content ${msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-assistant'}`}>
                            {msg.content}
                          </div>
                        </div>
                      ))}
                      {chatLoading && (
                        <div className="message-assistant">
                          <div className="chat-bubble-assistant flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('playground.thinking')}
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>

                  <div className="chat-input-container">
                    <div className="flex gap-2">
                      <Input
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                        placeholder={t('playground.placeholder')}
                        disabled={chatLoading}
                        className="flex-1 input-focus-ring"
                        data-testid="chat-input"
                      />
                      <Button
                        onClick={sendChatMessage}
                        disabled={chatLoading || !chatInput.trim()}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="chat-send-btn"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
